import React, { useContext } from 'react';
import Visible from '@99designs/apps/spa/pages/create/common/components/Visible';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import { BriefContext } from '@99designs/brief';
import { InteractiveAvatar } from '@99designs/designers';
import { AvatarPlaceholder } from '@99designs/frontend-components/components/AvatarPlaceholder';
import { getComponentClasses } from '@99designs/frontend-components/utils/app';
import ContactsDrawer from './ContactsDrawer';
import DesignerStack from './DesignerStack';
import { default as Tooltip } from './StoredPopoverCard';

interface TooltipCopy {
    heading: string;
    message: string;
    storageKey: string;
}

interface Props {
    onRemoveDesigner: (index: number) => void;
    selectDesignersCopy: TooltipCopy;
    confirmDesignersCopy: TooltipCopy;
    designers: { id: string; displayName: string }[];
    showTooltips?: boolean;
    brief: {
        id: string;
        designers: DesignerCard_UserFragment[];
        contacts: DesignerCard_UserFragment[];
    };
    isDesignerStep?: boolean;
    disableInteraction?: boolean;
}

function DesignerSelection({
    designers,
    onRemoveDesigner,
    selectDesignersCopy,
    confirmDesignersCopy,
    showTooltips = false,
    brief,
    isDesignerStep,
    disableInteraction,
}: Props) {
    const { disableContacts, totalAllowableDesigners } = useContext(BriefContext);
    const showContacts = brief.contacts.length !== 0 && !disableInteraction && !disableContacts;

    return (
        <div className="designer-selection">
            <Visible sizes={['small', 'medium']}>
                <DesignerStack brief={brief} disableInteraction={disableInteraction} />
            </Visible>

            <Visible sizes={['normal', 'large']}>
                <div className="designer-selection__item-list">
                    {designers.map((designer, idx) => (
                        <div
                            key={designer.id}
                            className="designer-selection__item"
                            data-testid="designer-selection-item"
                        >
                            {showTooltips && idx === totalAllowableDesigners - 1 ? (
                                <Tooltip
                                    heading={confirmDesignersCopy.heading}
                                    message={confirmDesignersCopy.message}
                                    storageKey={confirmDesignersCopy.storageKey}
                                    storageSource={localStorage}
                                >
                                    <InteractiveAvatar
                                        userId={designer.id}
                                        onRemoveClick={
                                            !disableInteraction ? onRemoveDesigner : undefined
                                        }
                                    />
                                </Tooltip>
                            ) : (
                                <InteractiveAvatar
                                    userId={designer.id}
                                    onRemoveClick={
                                        !disableInteraction ? onRemoveDesigner : undefined
                                    }
                                />
                            )}
                        </div>
                    ))}
                </div>
            </Visible>

            <Visible
                className={getComponentClasses(
                    'designer-section__action',
                    designers.length > 0 ? ['with-stack'] : []
                )}
                sizes={['small', 'medium']}
            >
                {brief.contacts.length !== 0 && (
                    <div data-testid="designer-section-action-small-screen">
                        <ContactsDrawer brief={brief} />
                    </div>
                )}
            </Visible>

            <Visible className="designer-section__action" sizes={['normal', 'large']}>
                {showContacts ? (
                    <ContactsDrawer brief={brief} />
                ) : (
                    isDesignerStep &&
                    designers.length < totalAllowableDesigners && (
                        <div key="placeholder" className="designer-selection__item">
                            {showTooltips ? (
                                <Tooltip
                                    heading={selectDesignersCopy.heading}
                                    message={selectDesignersCopy.message}
                                    storageKey={selectDesignersCopy.storageKey}
                                    storageSource={localStorage}
                                >
                                    <AvatarPlaceholder title={__('Select another designer.')} />
                                </Tooltip>
                            ) : (
                                <AvatarPlaceholder title={__('Select another designer.')} />
                            )}
                        </div>
                    )
                )}
            </Visible>
        </div>
    );
}

export default DesignerSelection;
